import React, { useMemo } from "react";
import { useAuth } from "context/AuthProvider";
import Card from "components/ui-elements/Card";
import logoPdf from "assets/logo_pdf.png";

const commonSections = [
  {
    title: "A. Pendahuluan",
    paragraphs: [
      "Sistem Informasi Manajemen Keolahragaan (SIMAKOR) adalah aplikasi yang digunakan untuk membantu pengelolaan data keolahragaan secara terstruktur. Data yang dikelola meliputi organisasi olahraga, cabang olahraga, nomor pertandingan, sumber daya manusia keolahragaan, kegiatan, peserta kejuaraan, sampai rekapitulasi medali.",
      "Panduan ini disusun untuk membantu pengguna memahami cara kerja SIMAKOR secara bertahap. Bagian awal menjelaskan hal-hal umum yang berlaku untuk semua pengguna. Bagian berikutnya menyesuaikan peran aktif pengguna yang sedang login, sehingga pengguna dapat fokus pada pekerjaan yang memang menjadi kewenangannya.",
      "Ada beberapa istilah yang akan sering ditemui. Peran adalah jenis akses pengguna, misalnya Admin KONI, Admin Induk Organisasi, Admin Klub, Admin Kejuaraan, Operator Cabor, atau Personal. Ruang kerja aktif adalah organisasi atau kejuaraan yang sedang digunakan saat bekerja. Tabel daftar data adalah halaman berisi daftar informasi yang biasanya dilengkapi pencarian, filter, nomor halaman, tombol tambah, tombol unduh PDF, tombol unduh XLS, dan tombol aksi.",
      "Cara membaca panduan ini sederhana. Baca bagian persiapan terlebih dahulu sebelum login, lanjutkan ke bagian navigasi untuk mengenali tampilan aplikasi, kemudian ikuti bagian pola operasional umum. Setelah itu, baca bagian operasional sesuai peran yang sedang digunakan.",
    ],
    notes: [
      "Semua perubahan data penting dilakukan melalui kotak dialog yang muncul di layar.",
      "Data yang tampil selalu mengikuti peran dan ruang kerja yang sedang aktif.",
      "Jika pengguna memiliki lebih dari satu peran, pastikan peran aktif sudah benar sebelum mulai bekerja.",
    ],
  },
  {
    title: "B. Persiapan dan Akses Sistem",
    images: ["guide-login.png"],
    paragraphs: [
      "SIMAKOR dapat digunakan melalui browser modern seperti Google Chrome, Microsoft Edge, atau Mozilla Firefox. Untuk pengalaman terbaik, gunakan laptop atau komputer desktop saat mengisi data dalam jumlah banyak, terutama data SDM, organisasi, kejuaraan, dan rekapitulasi medali. Penggunaan melalui tablet atau ponsel tetap memungkinkan, tetapi beberapa tabel dan kotak dialog akan lebih nyaman digunakan pada layar yang lebih lebar.",
      "Pastikan perangkat terhubung ke internet dengan koneksi stabil. Karena sistem menyimpan data secara online, gangguan koneksi saat menyimpan dapat membuat proses terasa lambat atau gagal. Sebelum menggunakan sistem, pastikan pengguna sudah memiliki username dan password yang aktif.",
      "Tautan akses aplikasi mengikuti alamat resmi yang diberikan oleh admin sistem. Gunakan hanya alamat aplikasi resmi agar data yang dibuka adalah data sistem yang benar.",
    ],
    steps: [
      "Buka browser, lalu akses alamat aplikasi SIMAKOR.",
      "Pada halaman login, masukkan username dan password.",
      "Klik tombol login, lalu tunggu sampai dashboard tampil.",
      "Jika sistem meminta ganti password, buka menu Akun Saya, Pengaturan & Bantuan, lalu Ubah Password.",
      "Jika pengguna memiliki lebih dari satu peran, gunakan tombol Peran untuk memilih ruang kerja yang benar.",
    ],
    notes: [
      "Untuk akun organisasi, username umumnya menggunakan email organisasi.",
      "Untuk akun personal, username menggunakan NIK.",
      "Password awal personal mengikuti tanggal lahir dengan format dua digit tanggal, dua digit bulan, empat digit tahun, misalnya 30011990.",
    ],
  },
  {
    title: "C. Navigasi dan Tampilan Utama",
    images: ["guide-dashboard.png"],
    paragraphs: [
      "Setelah berhasil login, pengguna akan masuk ke dashboard. Sidebar di sebelah kiri berisi Menu Utama dan Akun Saya. Menu Utama menampilkan fitur kerja sesuai peran, sedangkan Akun Saya berisi Profil Saya pada akun personal, kelompok Pengaturan & Bantuan, dan Logout. Di dalam Pengaturan & Bantuan tersedia Ubah Password, Panduan Penggunaan Sistem, dan Laporkan Masalah.",
      "Bagian atas aplikasi menampilkan tombol garis tiga untuk membuka atau menutup sidebar. Jika pengguna memiliki lebih dari satu peran, tombol Peran tersedia untuk mengganti ruang kerja. Di samping tombol garis tiga, sistem menampilkan ruang kerja aktif sebagai pengingat, misalnya nama KONI Kab/Kota, Induk Organisasi, Klub Olahraga, kejuaraan, atau organisasi terakhir yang terkait dengan akun personal.",
      "Setiap halaman data umumnya berbentuk tabel daftar data. Dari tabel ini, pengguna dapat mencari data, memakai filter, berpindah halaman, mengurutkan kolom tertentu, menambah data, mengunduh PDF, dan mengunduh XLS jika fitur tersebut tersedia. Kolom aksi berisi tombol seperti Detail, Edit, Delete, Aktifkan, Nonaktifkan, atau aksi khusus lain sesuai halaman.",
    ],
    steps: [
      "Gunakan sidebar untuk memilih menu kerja.",
      "Buka kelompok menu dengan mengklik judul menu yang memiliki icon folder.",
      "Klik submenu untuk membuka halaman fitur.",
      "Gunakan pencarian dan filter sebelum menambah atau mengubah data agar data yang sudah ada mudah dicek.",
      "Gunakan tombol Detail untuk memeriksa data lengkap sebelum melakukan perubahan penting.",
    ],
    notes: [
      "Menu yang terlihat dapat berbeda antar pengguna karena mengikuti peran aktif.",
      "Jika menu baru belum muncul setelah perubahan hak akses, logout lalu login kembali.",
      "Jika masih belum muncul, gunakan Akun Saya > Pengaturan & Bantuan > Laporkan Masalah dan pilih Tim KONI - Masalah Data agar peran dan ruang kerja pengguna dapat diperiksa.",
    ],
  },
  {
    title: "D. Pola Operasional Umum",
    images: ["guide-datalist-sdm.png", "guide-popup-form.png"],
    paragraphs: [
      "Sebagian besar proses input di SIMAKOR memakai pola yang sama. Pengguna menekan tombol tambah atau tombol aksi pada tabel daftar data, sistem membuka kotak dialog, pengguna mengisi kolom yang diperlukan, lalu menekan Simpan. Jika data berhasil disimpan, kotak dialog akan ditutup atau daftar data akan diperbarui.",
      "Pada proses edit, beberapa kolom dapat dikunci. Kolom yang dikunci biasanya adalah identitas utama yang tidak boleh berubah sembarangan, misalnya NIK, nomor induk, cabor pada keanggotaan tertentu, atau organisasi yang otomatis mengikuti akun login. Jika kolom terkunci, pengguna cukup membiarkannya apa adanya.",
      "Proses hapus pada data yang saling berhubungan umumnya tidak menghapus data utama. Misalnya pada SDM, hapus data hanya menghapus keanggotaan, bukan menghapus data orangnya. Hal ini penting karena satu orang dapat memiliki banyak riwayat keanggotaan, peran, organisasi, klub, dan kejuaraan.",
      "Data yang dapat digunakan sebagai pilihan pada proses lain adalah data yang masih aktif. Data yang sudah dinonaktifkan tetap tersimpan sebagai riwayat, tetapi tidak digunakan lagi sebagai pilihan saat mengisi formulir baru, memilih cabor, memilih organisasi, memilih klub, memilih nomor pertandingan, atau memilih data acuan lainnya.",
    ],
    steps: [
      "Sebelum menambah data, lakukan pencarian untuk memastikan data belum ada.",
      "Klik tombol Tambah, Lapor, Catat Medali, atau tombol lain sesuai halaman yang sedang digunakan.",
      "Isi kolom wajib dan perhatikan pilihan awal pada dropdown.",
      "Jika dropdown menyediakan pilihan Lainnya, sistem akan membuka kotak dialog untuk menambah pilihan baru.",
      "Klik Simpan setelah data lengkap.",
      "Jika muncul pesan validasi, perbaiki kolom yang ditandai tanpa menutup kotak dialog.",
      "Gunakan unduh PDF untuk laporan siap cetak dan unduh XLS untuk data lengkap yang akan diolah kembali.",
    ],
    notes: [
      "Pesan error dibuat sederhana agar mudah dipahami pengguna.",
      "Status aktif/nonaktif biasanya tidak diisi dari formulir, tetapi diubah melalui tombol aksi.",
      "Jika sebuah data tidak muncul pada pilihan, periksa apakah data tersebut masih berstatus aktif.",
      "Data tanggal pada formulir menggunakan format dd/mm/yyyy, sedangkan tampilan data menggunakan format panjang bahasa Indonesia.",
    ],
  },
  {
    title: "E. Cetak dan Ekspor Data",
    images: ["guide-export-data.png"],
    paragraphs: [
      "SIMAKOR menyediakan unduh PDF dan unduh XLS pada banyak halaman. PDF digunakan untuk laporan formal yang siap dicetak, sedangkan XLS digunakan untuk kebutuhan rekap, pemeriksaan data, atau pengolahan lanjutan menggunakan aplikasi spreadsheet. Karena kebutuhan PDF dan XLS berbeda, kolom yang tampil pada PDF dapat lebih ringkas, sementara XLS dapat memuat kolom yang lebih lengkap.",
      "Data yang dicetak atau diekspor mengikuti pencarian dan filter yang sedang digunakan pada halaman tersebut. Jika pengguna memilih filter cabor, status, kategori, klub, atau mengetik kata kunci pencarian, maka file PDF atau XLS yang dihasilkan hanya memuat data sesuai pilihan tersebut. Karena itu, pastikan filter sudah benar sebelum mengunduh laporan.",
      "Saat membuka PDF, tunggu hingga dokumen selesai dimuat. Pada koneksi lambat, dokumen dapat membutuhkan beberapa saat karena sistem menyiapkan kop laporan, logo, dan data. Jika PDF terlihat kosong sesaat, tutup lalu buka kembali setelah beberapa detik.",
      "Halaman Panduan Penggunaan Sistem ini dapat dicetak melalui tombol bergambar printer. Saat tombol tersebut digunakan, sistem menyembunyikan menu, header, footer, dan tombol navigasi, sehingga hasil cetak hanya memuat isi panduan.",
    ],
    steps: [
      "Buka halaman data yang ingin dicetak atau diekspor.",
      "Gunakan pencarian dan filter sesuai kebutuhan laporan.",
      "Periksa tabel daftar data untuk memastikan data yang tampil sudah sesuai.",
      "Klik tombol PDF untuk membuka laporan dalam format siap cetak.",
      "Klik tombol XLS jika data akan diperiksa atau diolah kembali menggunakan aplikasi spreadsheet.",
      "Jika hasil belum sesuai, tutup file, ubah pencarian atau filter pada halaman data, lalu unduh kembali.",
    ],
  },
];

const groupGuides = {
  SUPERADMIN: {
    title: "F. Operasional SUPERADMIN",
    images: ["guide-superadmin.png"],
    paragraphs: [
      "SUPERADMIN digunakan untuk pengaturan dan pengawasan sistem. Peran ini memiliki akses paling luas, sehingga sebaiknya digunakan secara hati-hati. SUPERADMIN dapat dipakai untuk menyiapkan akun, mengecek menu, memperbaiki peran pengguna, dan memeriksa data acuan sebelum pengguna lain bekerja dengan peran masing-masing.",
      "Menu Administrasi berisi pengaturan pengguna, kelompok pengguna, hak akses, menu, pengaturan aplikasi, catatan aktivitas, dan peran pengguna. Perubahan pada menu dan hak akses dapat memengaruhi tampilan semua pengguna. Karena itu, setelah mengubah menu atau hak akses, lakukan logout dan login ulang agar tampilan aplikasi diperbarui.",
      "SUPERADMIN juga dapat mengelola organisasi tingkat sistem seperti KONI Provinsi. Namun untuk pekerjaan operasional harian, gunakan peran Admin KONI Kab/Kota, Admin Induk Organisasi, atau Admin Klub agar alur kerja sesuai kewenangan pengguna sebenarnya.",
    ],
    steps: [
      "Gunakan Administrasi > User untuk memeriksa akun pengguna.",
      "Gunakan menu pengaturan peran pengguna atau User Context untuk menambah atau memperbaiki peran pengguna. Menu ini adalah tempat admin mengatur akun pengguna boleh masuk sebagai peran apa.",
      "Gunakan Administrasi > Menu untuk memastikan menu aktif dan terlihat.",
      "Gunakan Audit Log untuk menelusuri aksi penting jika terjadi perubahan data yang perlu dicek.",
    ],
    notes: [
      "Akun adminsimakor dapat memiliki peran tambahan untuk kebutuhan simulasi penggunaan, tetapi saat login pertama tetap masuk sebagai SUPERADMIN.",
      "Jangan menggunakan akun SUPERADMIN sebagai akun organisasi otomatis.",
    ],
  },
  ADMIN_KONI_KABKOTA: {
    title: "F. Operasional Admin KONI Kab/Kota",
    images: ["guide-admin-koni.png"],
    paragraphs: [
      "Admin KONI Kab/Kota adalah peran utama untuk operasional tingkat kabupaten/kota. Peran ini mengelola data organisasi di bawah KONI, termasuk induk organisasi cabor, klub olahraga, pengurus, SDM keolahragaan, serta kegiatan seperti Kejuaraan dan Pelatihan.",
      "Dashboard Admin KONI menampilkan ringkasan data sesuai KONI Kab/Kota yang sedang digunakan. Angka yang tampil adalah gambaran awal untuk mengetahui apakah data organisasi, cabor, nomor pertandingan, klub, pengurus, dan SDM sudah terisi. Jika dashboard kosong, pastikan peran aktif sudah benar dan data memang berada di bawah KONI Kab/Kota tersebut.",
      "Pada Data Organisasi, menu Profil digunakan untuk melihat profil KONI aktif dan memperbarui kontak seperti alamat, telepon, email, website, serta logo. Menu Pengurus digunakan untuk mencatat pengurus organisasi KONI. Saat menambah pengurus, sistem meminta NIK terlebih dahulu agar data personal dapat digunakan ulang bila sudah ada.",
      "Pada Data Cabang Olahraga, Admin KONI mengelola Induk Organisasi Cabor, Klub Olahraga, Cabang Olahraga, dan Nomor Pertandingan di wilayah kabupaten/kota yang sedang digunakan. Organisasi cabor berada di level kabupaten/kota, sedangkan klub berada di bawah induk organisasi. Saat membuat klub, pastikan induk organisasi dipilih dengan benar agar daftar cabor dan nomor pertandingan yang terkait dapat muncul.",
      "Pada SDM Keolahragaan, pengguna mengelola Atlet, Pelatih, Wasit, Juri, Manajer, Mekanik, dan Official. Alur entri dimulai dari memilih cabor atau induk organisasi sesuai formulir, lalu mengisi NIK. Jika NIK sudah dikenal sistem, data personal tampil otomatis dan tidak perlu diketik ulang. Jika NIK belum ada, pengguna mengisi data personal lengkap.",
      "Pada Kegiatan, Admin KONI dapat membuat Kejuaraan dan Pelatihan. Setelah kejuaraan dibuat, Admin KONI dapat menunjuk Admin Kejuaraan berdasarkan NIK personal. Orang yang ditunjuk akan mendapat akses untuk mengelola peserta dan medali pada kejuaraan tersebut.",
    ],
    steps: [
      "Mulai dari Dashboard untuk memastikan KONI Kab/Kota yang aktif sudah benar.",
      "Lengkapi Data Organisasi > Profil dan Pengurus.",
      "Tambahkan atau periksa Induk Organisasi Cabor.",
      "Tambahkan Klub Olahraga bila klub berada di bawah induk organisasi tersebut.",
      "Kelola SDM melalui menu Atlet, Pelatih, Wasit, Juri, Manajer, Mekanik, atau Official.",
      "Buat Kejuaraan melalui menu Kegiatan > Kejuaraan.",
      "Tambahkan Admin Kejuaraan melalui menu Kegiatan > Admin Kejuaraan.",
    ],
    notes: [
      "Admin KONI Kab/Kota hanya dapat melihat dan mengubah data di bawah KONI Kab/Kota aktif.",
      "Jika mengubah klub pada data SDM, pastikan klub tersebut berada di bawah KONI yang sedang digunakan.",
    ],
  },
  ADMIN_CABOR: {
    title: "F. Operasional Admin Induk Organisasi",
    images: ["guide-admin-cabor.png"],
    paragraphs: [
      "Admin Induk Organisasi bekerja pada induk organisasi cabang olahraga tertentu. Peran ini digunakan oleh pengurus organisasi cabang olahraga untuk mengelola profil organisasi, pengurus, Klub Olahraga di bawahnya, serta SDM Keolahragaan yang berada dalam cakupan cabor tersebut.",
      "Pada menu Data Organisasi, Admin Induk Organisasi dapat membuka Profil untuk melihat dan memperbarui logo serta kontak organisasi. Pengurus organisasi dicatat melalui menu Pengurus. Data pengurus tetap menggunakan data personal yang sama dengan SDM, sehingga satu orang tidak perlu dibuat berulang di sistem.",
      "Admin Induk Organisasi dapat mengelola Klub Olahraga di bawah induk organisasinya. Saat membuat atau mengubah klub, pastikan cabor yang dikelola klub sudah sesuai. Jika klub mengelola lebih dari satu cabor, pilihan cabor harus dicek dengan teliti karena data ini memengaruhi SDM dan Nomor Pertandingan yang dapat dipakai.",
      "Pada SDM Keolahragaan, Admin Induk Organisasi dapat mengelola Atlet, Pelatih, Wasit, Juri, Manajer, Mekanik, dan Official sesuai cabor yang dikelola. Jika SDM tidak berafiliasi dengan klub, data dapat disimpan langsung di bawah induk organisasi. Jika berafiliasi dengan klub, pilih klub yang sesuai dari daftar yang tersedia.",
      "Cabang Olahraga dan Nomor Pertandingan bersifat terbatas sesuai cabor yang dikelola. Gunakan filter cabor bila organisasi mengelola lebih dari satu cabor. Data nomor pertandingan yang tampil adalah data aktif dan sesuai cabor.",
    ],
    steps: [
      "Periksa Dashboard untuk melihat ringkasan cabor, nomor pertandingan, klub, pengurus, dan SDM.",
      "Lengkapi Profil organisasi dan logo bila belum sesuai.",
      "Catat Pengurus organisasi melalui Data Organisasi > Pengurus.",
      "Kelola Klub Olahraga di bawah induk organisasi.",
      "Kelola SDM sesuai peran melalui menu SDM Keolahragaan.",
      "Gunakan menu Data Cabang Olahraga untuk melihat cabor dan nomor pertandingan aktif.",
    ],
  },
  ADMIN_KLUB: {
    title: "F. Operasional Admin Klub Olahraga",
    images: ["guide-admin-klub.png"],
    paragraphs: [
      "Admin Klub Olahraga bekerja pada klub tertentu. Peran ini digunakan oleh pengelola klub untuk memperbarui profil klub, mencatat pengurus, dan mengelola SDM yang berafiliasi dengan klub tersebut.",
      "Pada Data Organisasi, menu Profil menampilkan data klub aktif. Pengguna dapat memperbarui logo, alamat, telepon, email, dan website. Identitas utama seperti nama klub, induk organisasi, jenis organisasi, dan status tidak diubah dari halaman Profil, melainkan oleh pengguna yang memiliki kewenangan pengelolaan organisasi.",
      "Saat menambah SDM dari akun klub, sistem otomatis mengaitkan data ke klub aktif. Pengguna tetap perlu memilih cabor bila klub mengelola lebih dari satu cabor. Untuk data personal yang sudah tersedia, pengguna cukup menambahkan keanggotaan atau peran, sedangkan data personalnya tetap terkunci agar tidak terjadi perubahan yang tidak disengaja.",
      "Cabang Olahraga dan Nomor Pertandingan pada Admin Klub hanya dapat dilihat. Fitur ini membantu klub melihat daftar cabor dan nomor yang relevan tanpa mengubah data acuan.",
    ],
    steps: [
      "Buka Dashboard untuk memastikan klub yang aktif sudah benar.",
      "Lengkapi Profil klub dan logo.",
      "Catat Pengurus klub melalui Data Organisasi > Pengurus.",
      "Tambahkan SDM klub melalui menu Atlet, Pelatih, Wasit, Juri, Manajer, Mekanik, atau Official.",
      "Gunakan Data Cabang Olahraga untuk melihat cabor dan nomor pertandingan yang relevan.",
    ],
    notes: [
      "Admin Klub hanya melihat data klub aktif dan data yang berada di bawah klub tersebut.",
      "Jika pilihan cabor kosong, pastikan klub sudah dihubungkan dengan cabor oleh induk organisasi atau Admin KONI.",
    ],
  },
  ADMIN_KEJUARAAN: {
    title: "F. Operasional Admin Kejuaraan",
    images: ["guide-admin-kejuaraan.png", "guide-medali.png"],
    paragraphs: [
      "Admin Kejuaraan bekerja pada satu kejuaraan tertentu. Nama kejuaraan aktif ditampilkan pada bagian atas aplikasi agar pengguna selalu mengetahui kegiatan yang sedang dikelola. Semua data peserta, kontingen, cabor, nomor pertandingan, SDM, tim, dan medali mengikuti kejuaraan tersebut.",
      "Menu Peserta Kejuaraan digunakan untuk menyiapkan data keikutsertaan. Proses ideal dimulai dari Kontingen, lalu Cabor, Nomor Pertandingan, kemudian SDM seperti Atlet, Pelatih, Wasit, Juri, Manajer, Mekanik, dan Official. Urutan ini penting karena pilihan nomor dan SDM bergantung pada cabor serta nomor yang sudah dipilih dalam kejuaraan.",
      "Pada submenu Kontingen, pengguna memilih kontingen dari daftar pilihan yang sesuai tingkat pertandingan kejuaraan. Jika kontingen belum tersedia, gunakan tombol tambah kontingen pada kotak pilihan. Kontingen KONI sendiri ditandai agar sistem mengetahui kontingen asal peserta yang dikelola oleh admin.",
      "Pada submenu Cabor dan Nomor Pertandingan, pemilihan dilakukan melalui kotak pilihan dengan checklist. Cabor yang sudah dipilih menjadi dasar filter nomor pertandingan. Nomor pertandingan yang tampil adalah nomor aktif dari cabor yang sudah masuk ke kejuaraan.",
      "Pada submenu Atlet, setiap atlet harus memiliki nomor pertandingan yang diikuti. Untuk nomor kategori perorangan, tim tidak perlu dipilih. Untuk nomor selain perorangan, pilih tim per nomor. Jika tim belum tersedia, gunakan pilihan Lainnya untuk menambah tim baru.",
      "Menu Rekapitulasi Medali digunakan setelah peserta dan nomor pertandingan siap. Pada Pencatatan Medali, pengguna mencatat medali berdasarkan kombinasi cabor, nomor pertandingan, dan jenis medali. Jika data untuk kombinasi tersebut sudah ada, formulir menampilkan data yang sudah tersimpan dalam mode baca agar tidak terjadi duplikasi. Perubahan data dilakukan melalui kotak detail medali.",
      "Medali bersama digunakan ketika satu jenis medali pada satu nomor pertandingan diberikan kepada lebih dari satu kontingen. Saat kondisi ini terjadi, pilih Ya pada pertanyaan Apakah Medali Bersama, lalu isi Kontingen 1 dan Kontingen 2 beserta atlet peraih masing-masing. Jika medali tidak bersama, pilih Tidak dan cukup isi satu kontingen serta atlet peraih. Data medali bersama tetap dihitung pada Rekap Medali Kontingen untuk setiap kontingen yang menerima medali tersebut.",
    ],
    steps: [
      "Pastikan nama kejuaraan aktif di bagian atas aplikasi sudah benar.",
      "Buka Peserta Kejuaraan > Kontingen dan pilih kontingen peserta.",
      "Buka Peserta Kejuaraan > Cabor dan pilih cabor yang dipertandingkan.",
      "Buka Peserta Kejuaraan > Nomor Pertandingan dan pilih nomor sesuai cabor.",
      "Buka Peserta Kejuaraan > Atlet untuk memilih atlet dan nomor yang diikuti.",
      "Tambahkan Pelatih, Wasit, Juri, Manajer, Mekanik, dan Official sesuai kebutuhan.",
      "Jika diperlukan, tambahkan Operator Cabor untuk membantu pengelolaan cabor tertentu.",
      "Catat hasil medali melalui Rekapitulasi Medali > Pencatatan Medali.",
      "Lihat hasil akhir melalui Rekapitulasi Medali > Rekap Medali Kontingen.",
    ],
    notes: [
      "Atlet wajib dikaitkan dengan nomor pertandingan.",
      "Pelatih, Wasit, Juri, Manajer, Mekanik, dan Official melekat pada cabor, bukan nomor pertandingan.",
      "Medali bersama dapat berisi dua kontingen dan dua atlet peraih.",
    ],
  },
  OPERATOR_KEJUARAAN_CABOR: {
    title: "F. Operasional Operator Kejuaraan Cabor",
    images: ["guide-operator-cabor.png"],
    paragraphs: [
      "Operator Kejuaraan Cabor membantu Admin Kejuaraan pada cabor tertentu. Akses operator dibatasi pada kejuaraan dan cabor yang ditugaskan. Peran ini berguna ketika satu kejuaraan melibatkan banyak cabor dan membutuhkan operator berbeda untuk masing-masing cabang.",
      "Operator perlu memastikan cabor aktif dan filter yang digunakan sudah sesuai. Jika data yang dicari tidak muncul, periksa kembali apakah cabor tersebut sudah dipilih pada peserta kejuaraan dan apakah nomor pertandingan sudah masuk ke kejuaraan.",
    ],
    steps: [
      "Pastikan kejuaraan dan cabor tugas sudah benar.",
      "Gunakan filter cabor dan nomor pertandingan untuk mempersempit data.",
      "Periksa data peserta yang sudah dimasukkan oleh Admin Kejuaraan.",
      "Laporkan ke Admin Kejuaraan bila ada cabor, nomor, atau SDM yang belum tersedia.",
    ],
  },
  PERSONAL: {
    title: "F. Operasional Akun Personal",
    images: ["guide-profil-personal.png"],
    paragraphs: [
      "Akun Personal digunakan oleh individu yang tercatat sebagai SDM Keolahragaan, seperti Atlet, Pelatih, Wasit, Juri, Manajer, Mekanik, atau Official. Akses personal bersifat terbatas dan lebih banyak digunakan untuk melihat profil serta riwayat keanggotaan.",
      "Menu Profil Saya menampilkan data pribadi dalam format profil. Pengguna dapat melihat identitas, data pekerjaan, orang tua, rekening, asuransi, serta riwayat keanggotaan. Foto profil dapat diperbarui melalui tombol Edit Foto bila fitur tersebut tersedia pada halaman.",
      "Data Cabang Olahraga pada akun personal hanya dapat dilihat. Personal dapat melihat Induk Organisasi Cabor, Klub Olahraga, Cabang Olahraga, Nomor Pertandingan, dan Riwayat Keanggotaan yang pernah atau sedang diikuti. Akses ini membantu personal memastikan datanya sudah tercatat dengan benar tanpa memberikan hak untuk mengubah data acuan.",
    ],
    steps: [
      "Buka Dashboard untuk melihat jumlah cabor, induk organisasi, klub, dan peran aktif.",
      "Buka Profil Saya untuk memeriksa data personal.",
      "Gunakan Edit Foto jika ingin memperbarui foto profil.",
      "Buka Riwayat Keanggotaan untuk melihat periode dan status keanggotaan.",
      "Laporkan ke admin organisasi jika ada data identitas atau keanggotaan yang perlu diperbaiki.",
    ],
  },
};

const troubleshootingSection = {
  title: "Troubleshooting",
  images: ["guide-laporkan-masalah.png"],
  paragraphs: [
    "Bagian ini berisi kendala yang paling sering terjadi saat menggunakan SIMAKOR dan langkah praktis untuk mengatasinya. Jika solusi di bawah belum menyelesaikan masalah, gunakan menu Akun Saya > Pengaturan & Bantuan > Laporkan Masalah. Pilih Tim Teknis - Masalah Error Sistem/Aplikasi untuk kendala error aplikasi, dan pilih Tim KONI - Masalah Data untuk kendala data, peran, ruang kerja, atau informasi yang perlu diperbaiki. Akun dan peran aktif pengguna tercatat otomatis oleh sistem, sehingga pengguna cukup menjelaskan menu yang dibuka, waktu kejadian, pesan yang tampil, serta melampirkan screenshot agar laporan lebih mudah ditindaklanjuti.",
  ],
  problems: [
    {
      issue: "Tidak bisa login.",
      solution: "Pastikan username dan password benar. Periksa apakah huruf besar, huruf kecil, angka, dan tanda baca sudah sesuai. Jika akun baru dibuat tetapi tetap belum bisa digunakan, buat laporan melalui Akun Saya > Pengaturan & Bantuan > Laporkan Masalah dengan tujuan Tim KONI - Masalah Data agar akun dan perannya dapat diperiksa.",
    },
    {
      issue: "Setelah login menu tidak lengkap atau peran belum muncul.",
      solution: "Logout lalu login kembali. Jika masih belum muncul, gunakan Akun Saya > Pengaturan & Bantuan > Laporkan Masalah dengan tujuan Tim KONI - Masalah Data. Jelaskan peran atau menu yang seharusnya tampil dan lampirkan screenshot halaman yang sedang dibuka.",
    },
    {
      issue: "Data yang dicari tidak tampil di tabel.",
      solution: "Periksa kata pencarian, filter status aktif/nonaktif, filter cabor, filter klub, atau peran yang sedang digunakan. Data mungkin berada di organisasi lain yang tidak termasuk kewenangan pengguna.",
    },
    {
      issue: "Tombol simpan tidak aktif.",
      solution: "Lengkapi kolom wajib. Pada beberapa formulir, tombol simpan baru aktif setelah NIK valid, data belum terdaftar, atau pilihan cabor, nomor pertandingan, dan kontingen sudah lengkap.",
    },
    {
      issue: "Muncul pesan tidak berhak melakukan proses.",
      solution: "Pastikan peran aktif sudah sesuai. Jika sedang mengubah data klub atau SDM, pastikan data tersebut berada di bawah organisasi yang sedang digunakan.",
    },
    {
      issue: "PDF kosong atau terasa lambat.",
      solution: "Tunggu beberapa detik lalu buka kembali. PDF dapat lambat bila sedang memuat logo atau data besar. Jika tetap kosong, reload halaman dan coba ulang.",
    },
    {
      issue: "Pilihan dropdown kosong.",
      solution: "Periksa urutan kerja. Misalnya pada kejuaraan, pilih cabor terlebih dahulu sebelum memilih nomor pertandingan; pada klub, pastikan induk organisasi dan cabor sudah diatur oleh admin.",
    },
    {
      issue: "Tanggal terlihat mundur satu hari.",
      solution: "Gunakan format tanggal dd/mm/yyyy pada formulir. Jika masalah muncul lagi, gunakan Akun Saya > Pengaturan & Bantuan > Laporkan Masalah dengan tujuan Tim Teknis - Masalah Error Sistem/Aplikasi dan sertakan contoh tanggal yang diinput serta screenshot hasil tampilannya.",
    },
    {
      issue: "Terjadi error sistem/aplikasi atau ada masalah data.",
      solution: "Gunakan menu Akun Saya > Pengaturan & Bantuan > Laporkan Masalah. Pilih Tim Teknis - Masalah Error Sistem/Aplikasi jika kendalanya berupa error, halaman tidak berjalan, tombol tidak merespon, PDF kosong, atau proses aplikasi gagal. Pilih Tim KONI - Masalah Data jika kendalanya berkaitan dengan data yang salah, data tidak muncul, atau data perlu diperbaiki oleh admin KONI. Tuliskan kronologi singkat, sebutkan menu yang dibuka, dan lampirkan screenshot agar laporan lebih mudah dipahami dan cepat ditindaklanjuti.",
    },
  ],
};

const guideItalicTerms = [
  "Tim Teknis - Masalah Error Sistem/Aplikasi",
  "Tim KONI - Masalah Data",
  "Panduan Penggunaan Sistem",
  "Data Cabang Olahraga",
  "Rekapitulasi Medali",
  "Pencatatan Medali",
  "Nomor Pertandingan",
  "Induk Organisasi",
  "Klub Olahraga",
  "Riwayat Keanggotaan",
  "Laporkan Masalah",
  "Pengaturan & Bantuan",
  "Akun Saya",
  "Menu Utama",
  "Profil Saya",
  "Ubah Password",
  "Data Organisasi",
  "Admin Kejuaraan",
  "Operator Cabor",
  "Tim KONI",
  "Dashboard",
  "Detail",
  "Edit",
  "Delete",
  "Aktifkan",
  "Nonaktifkan",
  "PDF",
  "XLS",
];

function renderGuideText(value) {
  const text = String(value || "");
  if (!text) return text;

  const terms = [...guideItalicTerms].sort((a, b) => b.length - a.length);
  const escaped = terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const regex = new RegExp(`(${escaped.join("|")})`, "g");

  return text.split(regex).map((part, idx) => {
    if (terms.includes(part)) {
      return <em key={`${part}-${idx}`}>{part}</em>;
    }
    return part;
  });
}

function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function GuideSection({ section }) {
  const id = slugify(section.title);
  return (
    <section className="guide-chapter" id={id}>
      <h5>{section.title}</h5>
      {(section.paragraphs || []).map((paragraph) => (
        <p key={paragraph}>{renderGuideText(paragraph)}</p>
      ))}
      {section.images?.length ? (
        <div className="guide-screenshot-list">
          {section.images.map((imageName) => (
            <img
              key={imageName}
              className={`guide-screenshot ${imageName === "guide-login.png" ? "guide-screenshot-login" : ""}`}
              src={`${process.env.PUBLIC_URL || ""}/assets/guide/${imageName}`}
              alt=""
            />
          ))}
        </div>
      ) : null}
      {section.steps?.length ? (
        <div className="guide-block">
          <div className="guide-block-title">Langkah kerja</div>
          <ol>
            {section.steps.map((item) => (
              <li key={item}>{renderGuideText(item)}</li>
            ))}
          </ol>
        </div>
      ) : null}
      {section.notes?.length ? (
        <div className="guide-block guide-note">
          <div className="guide-block-title">Catatan penting</div>
          <ul>
            {section.notes.map((item) => (
              <li key={item}>{renderGuideText(item)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {section.problems?.length ? (
        <div className="guide-problems">
          {section.problems.map((problem) => (
            <div className="guide-problem" key={problem.issue}>
              <strong>{renderGuideText(problem.issue)}</strong>
              <p>{renderGuideText(problem.solution)}</p>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function GuideToc({ sections }) {
  return (
    <section className="guide-chapter guide-toc-section" id="daftar-isi">
      <h5>Daftar Isi</h5>
      <div className="guide-toc">
        <ul>
          {sections.map((section) => (
            <li key={section.title}>
              <a href={`#${slugify(section.title)}`}>{section.title}</a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default function SystemGuidePage() {
  const { user } = useAuth();
  const groupName = String(user?.groupname || "").toUpperCase();
  const roleDescription = user?.group_description || user?.groupname || "Pengguna";
  const koniWilayah = String(user?.pdf_header?.koni_wilayah || "").trim();
  const activeContextName = String(user?.active_context?.context_name || "").trim();
  const pdfContextName = String(user?.pdf_header?.context_name || "").trim();
  const personalKoniName = String(user?.personal_koni_kabkota_name || "").trim();
  const formatKoniName = (value) => {
    const name = String(value || "").trim();
    if (!name) return "";
    if (/^(koni|komite olahraga nasional indonesia)/i.test(name)) return name;
    return `KONI ${name}`;
  };
  const isSuperadmin = groupName === "SUPERADMIN";
  const isKoniProvinsi = groupName === "ADMIN_KONI_PROVINSI";
  const fullKoniName = isSuperadmin
    ? ""
    : isKoniProvinsi
      ? formatKoniName(activeContextName || pdfContextName || koniWilayah)
      : formatKoniName(koniWilayah || personalKoniName);
  const currentYear = new Date().getFullYear();
  const groupSection = groupGuides[groupName] || {
    title: "F. Operasional Pengguna",
    images: ["guide-dashboard.png"],
    paragraphs: [
      "Bagian operasional menyesuaikan peran aktif pengguna. Jika isi bagian ini belum sesuai dengan pekerjaan pengguna, pastikan peran yang dipilih melalui tombol Peran sudah benar.",
    ],
  };
  const sections = useMemo(
    () => [...commonSections, groupSection, troubleshootingSection],
    [groupSection],
  );

  const printGuide = () => {
    window.print();
  };

  return (
    <>
      <style>
        {`
          .guide-chapter {
            border-bottom: 1px solid #edf0f5;
            margin-bottom: 22px;
            padding-bottom: 18px;
            scroll-margin-top: 80px;
            page-break-inside: avoid;
          }
          .guide-chapter h5 {
            color: #1f2937;
            font-weight: 700;
            margin-bottom: 12px;
          }
          .guide-chapter p {
            color: #374151;
            font-size: 0.9rem;
            line-height: 1.62;
            margin-bottom: 10px;
            text-align: justify;
          }
          .guide-block {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            margin-top: 12px;
            padding: 12px 14px;
          }
          .guide-note {
            background: #fffaf0;
            border-color: #f3d19e;
          }
          .guide-block-title {
            color: #111827;
            font-weight: 700;
            margin-bottom: 8px;
          }
          .guide-chapter ol,
          .guide-chapter ul {
            margin-bottom: 0;
            padding-left: 22px;
          }
          .guide-chapter li {
            margin-bottom: 7px;
            line-height: 1.5;
          }
          .guide-toc {
            background: #f9fafb;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 12px 14px;
          }
          .guide-toc ul {
            list-style: none;
            margin: 0;
            padding-left: 0;
          }
          .guide-toc li {
            margin-bottom: 8px;
          }
          .guide-toc a {
            color: #1f4f7a;
            font-weight: 600;
          }
          .guide-problem {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            margin-bottom: 10px;
            padding: 12px 14px;
            background: #fff;
          }
          .guide-problem p {
            margin-bottom: 0;
            text-align: left;
          }
          .guide-title {
            color: #1f2937;
            font-weight: 700;
            margin-bottom: 2px;
          }
          .guide-subtitle {
            color: #6b7280;
            font-size: 0.98rem;
            font-weight: 600;
          }
          .guide-title-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin-bottom: 18px;
          }
          .guide-screenshot-list {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 14px;
            margin: 14px 0 16px;
          }
          .guide-screenshot {
            border: 1px solid #cccccc;
            border-radius: 0;
            box-shadow: none;
            box-sizing: border-box;
            display: block;
            height: auto;
            max-width: 92%;
            width: auto;
          }
          .guide-screenshot-login {
            max-width: min(100%, 430px);
            width: auto;
          }
          .guide-print-button {
            min-width: 42px;
          }
          .guide-print-cover {
            display: none;
          }
          @media print {
            @page {
              size: A4 portrait;
              margin: 2cm;
            }
            body * {
              visibility: hidden !important;
            }
            html,
            body {
              margin: 0 !important;
              overflow: visible !important;
              width: auto !important;
            }
            .wrapper,
            .content-wrapper,
            .content,
            .container,
            .container-fluid {
              margin: 0 !important;
              max-width: none !important;
              min-height: auto !important;
              padding: 0 !important;
              width: auto !important;
            }
            .guide-print-area,
            .guide-print-area * {
              visibility: visible !important;
            }
            .guide-print-area {
              box-sizing: border-box !important;
              margin: 0 auto !important;
              max-width: 92% !important;
              overflow: visible !important;
              position: static !important;
              left: 0 !important;
              top: 0 !important;
              width: 92% !important;
              color: #111827 !important;
            }
            .guide-print-area * {
              box-sizing: border-box !important;
              overflow-wrap: break-word !important;
            }
            .guide-no-print {
              display: none !important;
            }
            .guide-title-row .guide-no-print {
              display: none !important;
            }
            .guide-title-row {
              display: none !important;
            }
            .guide-print-cover {
              align-items: center !important;
              display: flex !important;
              flex-direction: column !important;
              min-height: calc(297mm - 28mm) !important;
              padding: 18mm 0 6mm !important;
              page-break-after: always !important;
              text-align: center !important;
              text-transform: uppercase !important;
            }
            .guide-cover-top {
              margin-top: 0 !important;
              width: 100% !important;
            }
            .guide-cover-title {
              color: #111827 !important;
              font-size: 21pt !important;
              font-weight: 800 !important;
              letter-spacing: 0.8pt !important;
              line-height: 1.25 !important;
              margin: 0 0 5mm !important;
            }
            .guide-cover-system {
              color: #111827 !important;
              font-size: 22pt !important;
              font-weight: 800 !important;
              letter-spacing: 0.4pt !important;
              line-height: 1.25 !important;
              margin: 0 0 23mm !important;
              white-space: nowrap !important;
            }
            .guide-cover-role {
              color: #374151 !important;
              font-size: 21pt !important;
              font-weight: 700 !important;
              letter-spacing: 0.4pt !important;
              margin: 0 !important;
            }
            .guide-cover-logo-wrap {
              margin: 0 !important;
              width: 100% !important;
            }
            .guide-cover-logo-spacer {
              display: block !important;
              font-size: 11pt !important;
              line-height: 1.2 !important;
              margin: 0 !important;
            }
            .guide-cover-logo {
              display: block !important;
              height: auto !important;
              margin: 0 auto !important;
              max-height: none !important;
              max-width: none !important;
              object-fit: contain !important;
              width: 48mm !important;
            }
            .guide-cover-bottom {
              margin-top: auto !important;
              margin-bottom: 0 !important;
              width: 100% !important;
            }
            .guide-cover-credit {
              color: #111827 !important;
              font-size: 18pt !important;
              font-weight: 700 !important;
              letter-spacing: 0.3pt !important;
              margin: 0 0 12mm !important;
            }
            .guide-cover-koni {
              color: #111827 !important;
              font-size: 24pt !important;
              font-weight: 800 !important;
              letter-spacing: 0.4pt !important;
              line-height: 1.25 !important;
              margin: 0 0 4mm !important;
            }
            .guide-cover-year {
              color: #111827 !important;
              font-size: 18pt !important;
              font-weight: 800 !important;
              margin: 0 !important;
            }
            .guide-print-area .card,
            .guide-print-area .card-body {
              border: 0 !important;
              box-shadow: none !important;
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
            }
            .guide-chapter {
              break-inside: auto !important;
              margin-bottom: 18px !important;
              page-break-inside: auto !important;
              padding-bottom: 14px !important;
            }
            .guide-chapter p,
            .guide-chapter li {
              font-size: 14pt !important;
              line-height: 1.55 !important;
            }
            .guide-block,
            .guide-toc,
            .guide-problem {
              border: 1px solid #cccccc !important;
              box-sizing: border-box !important;
              background: #ffffff !important;
              break-inside: avoid !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
              max-width: 100% !important;
              page-break-inside: avoid !important;
              width: 100% !important;
            }
            .guide-screenshot-list {
              align-items: center !important;
              break-inside: avoid !important;
              gap: 8px !important;
              margin: 1.4em 0 12px !important;
              page-break-inside: avoid !important;
            }
            .guide-screenshot {
              border: 1px solid #cccccc !important;
              border-radius: 0 !important;
              box-shadow: none !important;
              box-sizing: border-box !important;
              display: block !important;
              height: auto !important;
              margin: 0 auto !important;
              max-height: 112mm !important;
              max-width: 88% !important;
              object-fit: contain !important;
              width: auto !important;
            }
            .guide-screenshot-login {
              max-width: 95mm !important;
              width: auto !important;
            }
            .guide-toc a {
              color: #111827 !important;
              text-decoration: none !important;
            }
          }
        `}
      </style>

      <div className="guide-print-area">
        <Card>
          <div className="guide-print-cover">
            <div className="guide-cover-top">
              <h1 className="guide-cover-system">Sistem Informasi Manajemen Keolahragaan (SIMAKOR)</h1>
              <p className="guide-cover-title">Panduan Penggunaan Sistem</p>
              <p className="guide-cover-role">{roleDescription}</p>
            </div>
            <div className="guide-cover-logo-wrap">
              {Array.from({ length: 19 }).map((_, idx) => (
                <p className="guide-cover-logo-spacer" key={`top-${idx}`}>&nbsp;</p>
              ))}
              <img className="guide-cover-logo" src={logoPdf} alt="Logo SIMAKOR" />
              {Array.from({ length: 19 }).map((_, idx) => (
                <p className="guide-cover-logo-spacer" key={`bottom-${idx}`}>&nbsp;</p>
              ))}
            </div>
            <div className="guide-cover-bottom">
              <p className="guide-cover-credit">oleh Tim Pengembang SIMAKOR</p>
              {fullKoniName ? <p className="guide-cover-koni">{fullKoniName}</p> : null}
              <p className="guide-cover-year">Tahun {currentYear}</p>
            </div>
          </div>
          <div className="guide-title-row">
            <div>
              <h4 className="guide-title">Panduan Penggunaan SIMAKOR</h4>
              <div className="guide-subtitle">{roleDescription}</div>
            </div>
            <button
              type="button"
              className="btn btn-default btn-sm guide-print-button guide-no-print"
              onClick={printGuide}
              title="Cetak panduan"
              aria-label="Cetak panduan"
            >
              <i className="fas fa-print" />
            </button>
          </div>
          <GuideToc sections={sections} />
          {sections.map((section) => (
            <GuideSection key={section.title} section={section} />
          ))}
        </Card>
      </div>
    </>
  );
}
